'use client';

import { LocalOverrideAdapter } from '@statsig/js-local-overrides';
import {
  type StatsigOptions,
  StatsigProvider,
  type StatsigUser,
  useClientBootstrapInit,
} from '@statsig/react-bindings';
import { env } from 'next-runtime-env';
import { type PropsWithChildren } from 'react';

interface StatsigClientProviderProps extends PropsWithChildren {
  readonly user: StatsigUser;
  readonly data?: string;
  readonly optionOverrides?: Partial<StatsigOptions>;
}

const getStatsigConfigEnv = (appEnv: string) => {
  if (appEnv === 'production') {
    return {
      disableLogging: true,
    };
  }

  return {
    // Allows setting local overrides for non-prod envs
    overrideAdapter: new LocalOverrideAdapter(),
  };
};

// Inner component that uses the hook - only rendered when env vars are present
function StatsigProviderInner({
  user,
  data,
  children,
  optionOverrides,
  clientKey,
  ouraEnv,
}: StatsigClientProviderProps & {
  readonly clientKey: string;
  readonly ouraEnv: string;
}): JSX.Element {
  const stableId = user.customIDs?.stableID;

  if (!stableId) {
    // eslint-disable-next-line no-console
    console.error('Missing stable id. Statsig will not bootstrap properly');
  }

  const options: StatsigOptions = {
    environment: { tier: ouraEnv },
    networkConfig: {
      // eslint-disable-next-line no-process-env
      preventAllNetworkTraffic: process.env.NODE_ENV === 'test',
    },
    ...getStatsigConfigEnv(ouraEnv),
    ...optionOverrides,
  };

  const client = useClientBootstrapInit(clientKey, user, data ?? '{}', options);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}

export default function StatsigProviderWrapper({
  user,
  data,
  children,
  optionOverrides,
}: StatsigClientProviderProps): JSX.Element {
  const clientKey = env('NEXT_PUBLIC_STATSIG_CLIENT_KEY');
  const ouraEnv = env('NEXT_PUBLIC_OURA_ENV');

  // If env vars are missing, render children without Statsig
  // This handles 404 pages and local dev without proper env setup
  if (!clientKey || !ouraEnv) {
    // eslint-disable-next-line no-console
    console.warn(
      'Statsig not initialized: missing NEXT_PUBLIC_STATSIG_CLIENT_KEY or NEXT_PUBLIC_OURA_ENV',
    );
    return <>{children}</>;
  }

  return (
    <StatsigProviderInner
      user={user}
      data={data}
      optionOverrides={optionOverrides}
      clientKey={clientKey}
      ouraEnv={ouraEnv}
    >
      {children}
    </StatsigProviderInner>
  );
}
